import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearDisability,
    clearMentalHealth,
    clearModStatus,
    loadDisabilityDetails,
    loadMentalHealthDetails,
    loadModStatusDetails
} from '../../../store/directusService';
import {
    updateDirectusContractSelections,
    updatePreSelects
} from '../../../utils/directusFunctions';
import {
    NO_CONTRACT_ERROR,
    NO_UPDATE_ERROR
} from '../../../utils/formValidation/commonErrorConstants';
import {
    DISABILITY_DETAILS_ERROR,
    MENTAL_HEALTH_DETAILS_ERROR,
    MOD_STATUS_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateCircumstancesAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const _DISABILITY = 'disability';
const _MENTAL_HEALTH = 'mental_health';
const _MOD_STATUS = 'mod_status';

const submitMessages = {
    disabilities: 'Disabilities have been updated',
    mentalHealth: 'Mental health has been updated',
    modStatus: 'MOD statuses have been updated'
};

const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];

const CircumstancesAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        selectedDisabilities: [],
        preSelectedDisabilityIds: [],
        preSelectedDisabilities: [],

        selectedMentalHealth: [],
        preSelectedMentalHealthIds: [],
        preSelectedMentalHealth: [],

        selectedModStatus: [],
        preSelectedModStatusIds: [],
        preSelectedModStatus: []
    };
    const [admin, setAdmin] = useState(initialState);
    const [keys, setKeys] = useState({ disabilities: '0', mentalHealth: '1', modStatus: '2' });
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const disabilityDetails = useSelector(
        (state) => state.entities.directusService.disabilityDetails
    );
    const mentalHealthDetails = useSelector(
        (state) => state.entities.directusService.mentalHealthDetails
    );
    const modStatusDetails = useSelector(
        (state) => state.entities.directusService.modStatusDetails
    );

    // USE EFFECTS
    useEffect(() => {
        if (!contractId) return;
        setAdmin(initialState);
        setKeys(clearKeys(keys));
        setErrors({});
        disabilityDetails?.length < 1 && dispatch(loadDisabilityDetails());
        mentalHealthDetails?.length < 1 && dispatch(loadMentalHealthDetails());
        modStatusDetails?.length < 1 && dispatch(loadModStatusDetails());
    }, [contractId]);

    useEffect(() => {
        if (disabilityDetails?.length && contractId) {
            const { preSelects, preSelectIds } = updatePreSelects(
                _DISABILITY,
                disabilityDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedDisabilityIds: preSelectIds,
                preSelectedDisabilities: preSelects
            }));
        }
    }, [disabilityDetails, contractId]);

    useEffect(() => {
        if (mentalHealthDetails?.length && contractId) {
            const { preSelects, preSelectIds } = updatePreSelects(
                _MENTAL_HEALTH,
                mentalHealthDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedMentalHealthIds: preSelectIds,
                preSelectedMentalHealth: preSelects
            }));
        }
    }, [mentalHealthDetails, contractId]);

    useEffect(() => {
        if (modStatusDetails?.length && contractId) {
            const { preSelects, preSelectIds } = updatePreSelects(
                _MOD_STATUS,
                modStatusDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedModStatusIds: preSelectIds,
                preSelectedModStatus: preSelects
            }));
        }
    }, [modStatusDetails, contractId]);

    useEffect(() => {
        setIsSubmitButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(submitMessages.disabilities)) {
            setAdmin((prev) => ({
                ...prev,
                preSelectedDisabilityIds: [],
                preSelectedDisabilities: [],
                selectedDisabilities: []
            }));
            dispatch(clearDisability());
            dispatch(loadDisabilityDetails());
        }
        if (successMessage.includes(submitMessages.mentalHealth)) {
            setAdmin((prev) => ({
                ...prev,
                preSelectedMentalHealthIds: [],
                preSelectedMentalHealth: [],
                selectedMentalHealth: []
            }));
            dispatch(clearMentalHealth());
            dispatch(loadMentalHealthDetails());
        }
        if (successMessage.includes(submitMessages.modStatus)) {
            setAdmin((prev) => ({
                ...prev,
                preSelectedModStatusIds: [],
                preSelectedModStatus: [],
                selectedModStatus: []
            }));
            dispatch(clearModStatus());
            dispatch(loadModStatusDetails());
        }
        setIsSubmitButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (disabilityDetails?.length < 1) return DISABILITY_DETAILS_ERROR;
        if (mentalHealthDetails?.length < 1) return MENTAL_HEALTH_DETAILS_ERROR;
        if (modStatusDetails?.length < 1) return MOD_STATUS_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value, errorKey) => {
        clearError(errorKey ?? key);
        setAdmin((admin) => ({ ...admin, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsSubmitButtonDisabled(true);
        let isUpdated = false;
        const newErrors = validate(admin);
        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);
            return;
        }
        const isUpdatedDisability = updateDirectusContractSelections(
            _DISABILITY,
            admin.preSelectedDisabilityIds,
            admin.preSelectedDisabilities,
            admin.selectedDisabilities,
            contractId,
            submitMessages.disabilities,
            dispatch
        );
        const isUpdatedMentalHealth = updateDirectusContractSelections(
            _MENTAL_HEALTH,
            admin.preSelectedMentalHealthIds,
            admin.preSelectedMentalHealth,
            admin.selectedMentalHealth,
            contractId,
            submitMessages.mentalHealth,
            dispatch
        );
        const isUpdatedModStatus = updateDirectusContractSelections(
            _MOD_STATUS,
            admin.preSelectedModStatusIds,
            admin.preSelectedModStatus,
            admin.selectedModStatus,
            contractId,
            submitMessages.modStatus,
            dispatch
        );
        if (isUpdatedDisability || isUpdatedMentalHealth || isUpdatedModStatus) isUpdated = true;
        !isUpdated &&
            setErrors((prev) => ({ ...prev, button: { error: true, message: NO_UPDATE_ERROR } }));
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            onSubmit={onSubmit}
            className={form.formWrapper}
            data-testid="form_start_circumstances_admin">
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <MultiSelect
                        id="disabilities"
                        key={keys.disabilities}
                        label="Disability"
                        placeholder="Select disabilities..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={disabilityDetails || []}
                        preSelectedIds={admin.preSelectedDisabilityIds}
                        preSelects={admin.preSelectedDisabilities}
                        error={errors.disabilities}
                        onChange={(chosenIds) =>
                            onUpdate('selectedDisabilities', chosenIds, 'disabilities')
                        }
                    />
                    <MultiSelect
                        id="mentalHealth"
                        key={keys.mentalHealth}
                        label="Mental Health"
                        placeholder="Select mental health problems..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={mentalHealthDetails || []}
                        preSelectedIds={admin.preSelectedMentalHealthIds}
                        preSelects={admin.preSelectedMentalHealth}
                        error={errors.mentalHealth}
                        onChange={(chosenIds) =>
                            onUpdate('selectedMentalHealth', chosenIds, 'mentalHealth')
                        }
                    />
                </div>

                <div className={form.formColumn}>
                    <MultiSelect
                        id="modStatus"
                        key={keys.modStatus}
                        label="MOD Status"
                        placeholder="Select MOD Statuses..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={modStatusDetails || []}
                        preSelectedIds={admin.preSelectedModStatusIds}
                        preSelects={admin.preSelectedModStatus}
                        onChange={(chosenIds) => onUpdate('selectedModStatus', chosenIds)}
                    />
                </div>
            </div>
            <div>
                <p>Health Comment</p>
                <p>Criminal Record</p>
                <p>Criminal Record Comment</p>
                <p>Criminal Conviction Spent</p>
                <p>Required Adjustments</p>
                <p>Additional Contact</p>
            </div>
            <Button
                id={'updateCircumstancesButton'}
                content={'UPDATE CIRCUMSTANCES'}
                disabled={!hasRole(acceptedRoles, roles) || isSubmitButtonDisabled}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

CircumstancesAdmin.propTypes = {
    contractId: PropTypes.string
};

export default CircumstancesAdmin;
